import { API_PREFIX } from '@/config/api'
import router from '@/router'
import type { AdvancedEventFilters } from '@/types/filters/EventFilters'
import type { EventCreateModel } from '@/types/models/EventCreateModel'
import type { EventModel } from '@/types/models/EventModel'
import { api } from '@rfc/rfc-components-library/helpers'
import { ADMIN_ROUTE } from '@rfc/rfc-components-library/router'
import type {
    ExtendedPrimitives,
    FilterType,
    PaginationDataType,
    PaginationType,
    SortKeyType
} from '@rfc/rfc-components-library/types'
import { buildQueryUrl } from '@rfc/rfc-components-library/utils'
import type { AxiosResponse } from 'axios'

const API_EVENT_PREFIX = `${API_PREFIX}/events`
const API_ADMIN_EVENT_PREFIX = `${API_PREFIX}${ADMIN_ROUTE}/events`

const eventApi = {
    getEvent: (id: number) => api.get(`${API_EVENT_PREFIX}/${id}`),
    getEvents: (
        params?: Partial<
            PaginationType &
                FilterType<Partial<EventModel> & AdvancedEventFilters> & { sort: SortKeyType }
        >,
        noQueryParam?: boolean
    ): Promise<PaginationDataType<EventModel[]>> =>
        api
            .get(`${API_EVENT_PREFIX}${buildQueryUrl(params, noQueryParam, router)}`)
            .then(response => response.data),
    createEvent: (eventData: EventCreateModel): Promise<AxiosResponse<EventModel>> =>
        api.post(API_EVENT_PREFIX, eventData),
    updateEvent: (id: number, eventData: any) => api.patch(`${API_EVENT_PREFIX}/${id}`, eventData),
    deleteEvent: (id: number) => api.delete(`${API_EVENT_PREFIX}/${id}`),

    setEventFilters: (id: number, data: Record<string, ExtendedPrimitives>) =>
        api.patch(`${API_EVENT_PREFIX}/${id}`, data),
    setGuntime: (id: number, guntimes: { name: string; start_at: Date }[]) =>
        api.post(`${API_EVENT_PREFIX}/${id}/guntimes`, guntimes),
    syncCheckpoints: (id: number) => api.post(`${API_EVENT_PREFIX}/${id}/sync`),

    getAdminEvents: (
        params?: Partial<
            PaginationType &
                FilterType<Partial<EventModel> & AdvancedEventFilters> & { sort: SortKeyType }
        >,
        noQueryParam?: boolean
    ): Promise<PaginationDataType<EventModel[]>> =>
        api
            .get(`${API_ADMIN_EVENT_PREFIX}${buildQueryUrl(params, noQueryParam, router)}`)
            .then(response => response.data),

    setCheckpointsPositions: (eventId: number, checkpointsIds: number[]) =>
        api.post(`${API_EVENT_PREFIX}/${eventId}/checkpoint-positions`, {
            checkpoints: checkpointsIds.map(id => ({ id }))
        }),

    getDeviceDetectionsCount: (
        event_id: number,
        checkpoint_id: number,
        device_id: number
    ): Promise<{ count: number }> =>
        api
            .get(
                `${API_EVENT_PREFIX}/${event_id}/checkpoints/${checkpoint_id}/devices/${device_id}/detections-count`
            )
            .then(res => res.data)
}

export default eventApi
